import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { PageHeader, Btn, Modal, Field, Input, EmptyState } from "../components/UI";
import { Plus, Pencil, Trash2 } from "lucide-react";

/**
 * Generic CRUD page for simple business entities.
 * Used for: shippers, consignees, branches, vehicles, agents.
 * Talks DIRECTLY to Supabase — Row Level Security enforces that each
 * company only ever sees/edits its own rows. No custom backend involved.
 */
export default function CrudPage({ title, subtitle, table, fields, testIdPrefix }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const { data, error } = await supabase.from(table).select("*").order("created_at", { ascending: false });
    if (error) { setErr(error.message); return; }
    setItems(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [table]);

  const openCreate = () => {
    setEdit(null);
    setForm(fields.reduce((a, f) => ({ ...a, [f.name]: f.default ?? "" }), {}));
    setErr("");
    setOpen(true);
  };
  const openEdit = (it) => { setEdit(it); setForm(it); setErr(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0); });
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.company_id; // RLS/trigger territory — never set from client

    if (edit) {
      const { error } = await supabase.from(table).update(payload).eq("id", edit.id);
      if (error) { setErr(error.message); return; }
    } else {
      const { error } = await supabase.from(table).insert({ ...payload, company_id: user.company_id });
      if (error) { setErr(error.message); return; }
    }
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) { alert(error.message); return; }
    load();
  };

  const visible = items.filter(
    (it) => !q || JSON.stringify(it).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Layout>
      <PageHeader title={title} subtitle={subtitle}
        actions={<Btn data-testid={`${testIdPrefix}-add`} onClick={openCreate}><Plus size={16} className="inline -mt-0.5 mr-1"/>Add</Btn>} />
      <div className="lt-card p-4 mb-4 flex items-center gap-3">
        <input data-testid={`${testIdPrefix}-search`} placeholder="Search..." value={q} onChange={e=>setQ(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-sky-500 w-64"/>
        <span className="text-xs text-slate-500">{visible.length} record(s)</span>
      </div>
      {visible.length === 0 ? (
        <EmptyState title={`No ${title.toLowerCase()} yet`} action={<Btn onClick={openCreate}><Plus size={16} className="inline -mt-0.5 mr-1"/>Add new</Btn>}/>
      ) : (
        <div className="lt-card overflow-hidden">
          <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
              <tr>
                {fields.filter(f=>!f.hideInList).map(f => <th key={f.name} className="text-left px-4 py-2.5">{f.label}</th>)}
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(it => (
                <tr key={it.id} className="border-t hover:bg-slate-50">
                  {fields.filter(f=>!f.hideInList).map(f => <td key={f.name} className="px-4 py-2.5 text-slate-700">{String(it[f.name] ?? "")}</td>)}
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={()=>openEdit(it)} className="p-1.5 hover:bg-sky-100 rounded text-sky-700" data-testid={`${testIdPrefix}-edit-${it.id}`}><Pencil size={15}/></button>
                    <button onClick={()=>del(it.id)} className="p-1.5 hover:bg-red-100 rounded text-red-600 ml-1" data-testid={`${testIdPrefix}-delete-${it.id}`}><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={()=>setOpen(false)} title={`${edit?"Edit":"Add"} ${title}`} size="lg">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(f => (
            <Field key={f.name} label={f.label} className={f.full?"md:col-span-2":""}>
              <Input type={f.type || "text"} value={form[f.name] ?? ""} required={f.required}
                data-testid={`${testIdPrefix}-input-${f.name}`}
                onChange={e=>setForm({...form, [f.name]: e.target.value})}/>
            </Field>
          ))}
          {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Btn>
            <Btn type="submit" data-testid={`${testIdPrefix}-save`}>{edit ? "Update" : "Create"}</Btn>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}

export const SHIPPER_FIELDS = [
  {name:"company_name", label:"Company Name", required:true},
  {name:"gstin", label:"GSTIN"},
  {name:"contact_person", label:"Contact Person"},
  {name:"mobile", label:"Mobile"},
  {name:"email", label:"Email"},
  {name:"address", label:"Address", full:true, hideInList:true},
  {name:"city", label:"City"},
  {name:"state", label:"State"},
  {name:"pincode", label:"Pincode"},
];

export const BRANCH_FIELDS = [
  {name:"name", label:"Branch Name", required:true},
  {name:"city", label:"City", required:true},
  {name:"state", label:"State"},
  {name:"address", label:"Address", full:true, hideInList:true},
  {name:"contact", label:"Contact"},
];

export const VEHICLE_FIELDS = [
  {name:"vehicle_number", label:"Vehicle Number", required:true},
  {name:"vehicle_type", label:"Vehicle Type"},
  {name:"driver_name", label:"Driver Name"},
  {name:"driver_contact", label:"Driver Contact"},
  {name:"insurance_number", label:"Insurance Number"},
  {name:"fitness_expiry", label:"Fitness Expiry", type:"date"},
  {name:"status", label:"Status", default:"available"},
];

export const AGENT_FIELDS = [
  {name:"name", label:"Agent Name", required:true},
  {name:"service_type", label:"Service Type"},
  {name:"rate_per_kg", label:"Rate per KG", type:"number"},
  {name:"contact", label:"Contact"},
  {name:"email", label:"Email"},
];
